import { styled } from "goober";
import { Fragment, useEffect, useRef } from "react";
import { formatRelativeTime } from "../../lib/formatRelativeTime";
import { makeUrl, routes } from "../../lib/urls";
import { ClickButton } from "../ClickButton";
import { ConnectorDropdown, Divider, SectionLabel } from "../ConnectorDropdown";
import { Icon } from "../Icon";
import { Link } from "../Link";
import { Text } from "../Text";
import { chatLabel, pickChatIconColor } from "./pickChatIconColor";

const RECENTLY_CLOSED_WINDOW_MS = 96 * 60 * 60 * 1000;

type Props = {
  opener: React.ReactNode;
  tabs: readonly {
    id: string;
    name: string;
    lastActivityAt: number | null;
  }[];
  activeTabId: string | null;
  userId?: string;
  closedChats: readonly {
    id: string;
    name: string;
    closedAt: number;
    lastActivityAt: number;
  }[];
  onReopenChat?: ((id: string) => void) | undefined;
  onNewTab?: (() => void) | undefined;
};

const rowStyles = `
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  box-sizing: border-box;
  padding: 6px 8px;
  border-radius: 8px;
  cursor: pointer;
  text-align: left;
  text-decoration: none;

  &:hover,
  &[data-highlighted] {
    background: var(--alpha4);
  }
`;

const OpenChatLink = styled(Link)`
  ${rowStyles}
`;

const ClosedChatButton = styled(ClickButton)`
  ${rowStyles}
`;

const NewChatRow = styled(ClickButton)`
  ${rowStyles}
`;

function ChatRowContent({
  id,
  name,
  secondary,
  active,
}: {
  id: string;
  name: string;
  secondary?: string;
  active?: boolean;
}) {
  return (
    <>
      <Icon
        name="ask"
        size={16}
        color={`var(--${pickChatIconColor(id)}-600)`}
        style={{ flexShrink: 0 }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <Text variant="text-regular" color="var(--text-primary)" ellipsis>
          {chatLabel(name)}
        </Text>
        {secondary && (
          <Text variant="text-small" color="var(--text-secondary)" ellipsis>
            {secondary}
          </Text>
        )}
      </div>
      {active && (
        <Icon
          name="check"
          size={12}
          color="var(--text-secondary)"
          style={{ flexShrink: 0 }}
        />
      )}
    </>
  );
}

export function ChatTabsDropdown({
  opener,
  tabs,
  activeTabId,
  userId,
  closedChats,
  onReopenChat,
  onNewTab,
}: Props) {
  const openRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    // Match on e.code, not e.key: on macOS Option composes a glyph (⇧⌥C → Ç).
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.shiftKey &&
        e.altKey &&
        !e.metaKey &&
        !e.ctrlKey &&
        e.code === "KeyC"
      ) {
        e.preventDefault();
        openRef.current?.();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <ConnectorDropdown
      opener={opener}
      align="left"
      searchPlaceholder="Search chats…"
      openRef={openRef}
    >
      {(query, close) => {
        const lowerQuery = query.toLowerCase().trim();
        const matches = (name: string) =>
          chatLabel(name).toLowerCase().includes(lowerQuery);
        // oxlint-disable-next-line react/react-compiler -- blocks React Compiler; remove when fixed
        const cutoff = Date.now() - RECENTLY_CLOSED_WINDOW_MS;

        const sections = [
          {
            label: "Open chats",
            rows: tabs
              .filter((tab) => matches(tab.name))
              .map((tab) => (
                <OpenChatLink
                  key={tab.id}
                  data-nav-row=""
                  to={makeUrl(routes.chats, { tabId: tab.id, userId })}
                  onClick={() => close()}
                >
                  <ChatRowContent
                    id={tab.id}
                    name={tab.name}
                    secondary={
                      tab.lastActivityAt == null
                        ? undefined
                        : formatRelativeTime(tab.lastActivityAt)
                    }
                    active={tab.id === activeTabId}
                  />
                </OpenChatLink>
              )),
          },
          {
            label: "Recently closed",
            rows: onReopenChat
              ? closedChats
                  .filter(
                    (chat) => chat.closedAt > cutoff && matches(chat.name)
                  )
                  .map((chat) => (
                    <ClosedChatButton
                      key={chat.id}
                      data-nav-row=""
                      onClick={() => {
                        onReopenChat(chat.id);
                        close();
                      }}
                    >
                      <ChatRowContent
                        id={chat.id}
                        name={chat.name}
                        secondary={formatRelativeTime(chat.lastActivityAt)}
                      />
                    </ClosedChatButton>
                  ))
              : [],
          },
        ].filter((section) => section.rows.length > 0);

        if (sections.length === 0) {
          if (!onNewTab) return null;
          return (
            <NewChatRow
              data-nav-row=""
              onClick={() => {
                onNewTab();
                close();
              }}
            >
              <Icon name="plus" size={16} style={{ flexShrink: 0 }} />
              <Text variant="text-regular" color="var(--text-primary)" ellipsis>
                Start a new chat
              </Text>
            </NewChatRow>
          );
        }

        return sections.map((section, i) => (
          <Fragment key={section.label}>
            {i > 0 && <Divider />}
            <SectionLabel>
              <Text variant="text-small" color="var(--text-secondary)">
                {section.label}
              </Text>
            </SectionLabel>
            {section.rows}
          </Fragment>
        ));
      }}
    </ConnectorDropdown>
  );
}
